import { type ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import styled, { css } from 'styled-components';

const ROTATION_LENGTH = 1200;
const DURATION = '0.8s';

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  width: auto;
  height: auto;
  min-height: 0;
  min-width: 0;
`;

const Option = styled.div<{ $noFade: boolean }>`
  flex: none;
  display: flex;
  align-items: center;
  overflow: hidden;
  opacity: 0;
  min-height: 0;
  min-width: 0;

  &:first-child {
    opacity: 1;
  }

  ${({ $noFade }) =>
    $noFade &&
    css`
      opacity: 1;
    `}

  label {
    width: 100%;
    height: 100%;
  }

  span {
    display: flex;
    align-items: center;
    width: 100% !important;
    height: 100% !important;
    margin: 0 !important;
    vertical-align: unset !important;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
  }
`;

const Wrapper = styled.div<{
  $debugMode: boolean;
  $directionHorizontal: boolean;
  $isAnimating: boolean;
  $noFade: boolean;
}>`
  display: flex;
  overflow: hidden;
  position: relative;
  flex: none;
  min-height: 0;
  min-width: 0;

  ${({ $isAnimating }) =>
    $isAnimating &&
    css`
      ${Inner} {
        transition: transform ${DURATION}
          ${({ theme }) => theme.animation.EASING};
      }
    `}

  ${({ $isAnimating, $noFade }) =>
    $isAnimating &&
    !$noFade &&
    css`
      ${Inner} {
        ${Option} {
          &:first-child {
            transition: opacity calc(${DURATION} / 2.2)
              ${({ theme }) => theme.animation.EASING};
            opacity: 0;
          }

          &:nth-child(2) {
            transition: opacity calc(${DURATION} * 2.2)
              ${({ theme }) => theme.animation.EASING};
            opacity: 1;
          }
        }
      }
    `}

    ${({ $debugMode }) =>
    $debugMode &&
    css`
      overflow: visible !important;

      ${Option} {
        overflow: visible !important;
        opacity: 1 !important;
      }
    `}

    ${({ $directionHorizontal }) =>
    $directionHorizontal &&
    css`
      ${Inner} {
        flex-direction: row;
      }
    `}
`;

interface RotatorProps {
  debugMode?: boolean;
  direction?: 'vertical' | 'horizontal';
  itemHeight: number;
  itemWidth: number;
  noFade?: boolean;
  options: (string | ReactNode)[];
  rotationLength?: number;
  startDelay?: number;
}

export const Rotator: React.ComponentType<RotatorProps> = React.memo(
  ({
    direction = 'vertical',
    options,
    itemHeight,
    itemWidth,
    noFade = false,
    debugMode = false,
    startDelay,
    rotationLength
  }) => {
    const rotatorRef = useRef<HTMLDivElement>(null);
    const [orbits, setOrbits] = useState(0);
    const [translatePos, setTranslatePos] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [optionList, setOptionList] =
      useState<(string | ReactNode)[]>(options);

    const height = useMemo(() => {
      return itemHeight;
    }, [itemHeight]);

    const width: number = useMemo(() => {
      return itemWidth;
    }, [itemWidth]);

    const calcRotationLength: number = useMemo(() => {
      return rotationLength || ROTATION_LENGTH;
    }, [rotationLength]);

    // Initial load
    useEffect(() => {
      if (options) {
        setOptionList(options);

        // Start rotator
        setTimeout(() => {
          setOrbits(prev => prev + 1);
        }, startDelay || 0);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // On orbit change
    useEffect(() => {
      if (height && width && options && orbits > 0) {
        setTimeout(() => {
          // Go to the next one
          setTranslatePos(direction === 'vertical' ? height : width);
          setIsAnimating(true);

          // Reset rotator
          setTimeout(() => {
            const newOptions: (string | ReactNode)[] = [...optionList];
            newOptions.push(newOptions.shift()); // Move first to the end
            setOptionList(newOptions);
            setTranslatePos(0);
            setIsAnimating(false);
            setOrbits(prev => prev + 1); // Mark as a complete orbit
          }, calcRotationLength);
        }, calcRotationLength);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orbits]);

    const translateStyle =
      direction === 'vertical'
        ? `translateY(-${translatePos}px)`
        : `translateX(-${translatePos}px)`;

    return (
      <Wrapper
        ref={rotatorRef}
        $debugMode={debugMode}
        $directionHorizontal={direction === 'horizontal'}
        $isAnimating={isAnimating}
        $noFade={noFade}
        style={{ width: width, height: height }}>
        <Inner style={{ transform: translateStyle }}>
          {optionList.map((item, index) => (
            <Option
              key={index}
              $noFade={noFade}
              data-attr-index={index}
              style={{ width: width, height: height }}>
              <label>{item}</label>
            </Option>
          ))}
        </Inner>
      </Wrapper>
    );
  }
);
